// =====================================================================
// DocumentList (PME Sprint 3a-ii / S52) — aba "Documentos" do paciente.
// Anexar exames (PDF/imagem) + listar + ver/baixar + excluir.
// 🔒 LGPD: arquivos servidos auth-gated (blob com token via GET /ehr/files/:id);
// multi-tenant + audit no backend. Reusa a infra de storage privado da foto.
// =====================================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import ImageIcon from "@material-ui/icons/Image";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import VisibilityIcon from "@material-ui/icons/Visibility";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FindInPageIcon from "@material-ui/icons/FindInPage";
import { toast } from "react-toastify";

import {
  listPatientDocuments,
  uploadPatientDocument,
  getEhrFileObjectUrl,
  deleteEhrFile,
  validateDocFile,
  docErrorMessage,
  interpretExam,
  DOC_ACCEPT
} from "../../services/fileService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  title: { display: "flex", alignItems: "center", gap: theme.spacing(1), fontWeight: 600 },
  card: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { fontWeight: 600, wordBreak: "break-word" },
  meta: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 2 },
  desc: { fontSize: 13.5, marginTop: 4, whiteSpace: "pre-wrap" },
  actions: { display: "flex", gap: theme.spacing(0.5), flexShrink: 0 },
  empty: { color: theme.palette.text.secondary, fontStyle: "italic", padding: theme.spacing(2, 0) },
  hint: { color: theme.palette.text.secondary, marginTop: theme.spacing(1.5) }
}));

const pad = (n) => String(n).padStart(2, "0");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const isPdf = (doc) =>
  doc.fileType === "exam_pdf" || /\.pdf$/i.test(doc.fileName || "");
const isImage = (doc) =>
  /\.(png|jpe?g|webp|gif)$/i.test(doc.fileName || "") || doc.fileType === "photo";

const typeLabel = (doc) => {
  if (isPdf(doc)) return "PDF";
  if (isImage(doc)) return "Imagem";
  return "Documento";
};

const TypeIcon = ({ doc }) => {
  if (isPdf(doc)) return <PictureAsPdfIcon style={{ color: "#d32f2f" }} />;
  if (isImage(doc)) return <ImageIcon style={{ color: "#1976d2" }} />;
  return <InsertDriveFileIcon color="action" />;
};

const gravLabel = (g) => (g === "urgente" ? "🔴 Urgente" : g === "atencao" ? "🟡 Atenção" : "🟢 Normal");
const gravStyle = (g) =>
  g === "urgente"
    ? { background: "#ffebee", color: "#c62828" }
    : g === "atencao"
    ? { background: "#fff8e1", color: "#8d6e00" }
    : { background: "#e8f5e9", color: "#2e7d32" };

const DocumentList = ({ patientId }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const inputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingFile, setPendingFile] = useState(null);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [interpretTarget, setInterpretTarget] = useState(null);
  const [interpreting, setInterpreting] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  const [interpretErr, setInterpretErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPatientDocuments(patientId);
      setDocuments((data && data.documents) || []);
    } catch (err) {
      toast.error(docErrorMessage(err));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    const verr = validateDocFile(file);
    if (verr) {
      toast.error(verr);
      return;
    }
    setPendingFile(file);
    setDescription("");
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      await uploadPatientDocument(patientId, pendingFile, description.trim());
      toast.success("Exame anexado.");
      setPendingFile(null);
      setDescription("");
      await load();
    } catch (err) {
      toast.error(docErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc) => {
    setBusyId(doc.id);
    try {
      const url = await getEhrFileObjectUrl(doc.id);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(docErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const url = await getEhrFileObjectUrl(doc.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.fileName || `exame-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(docErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleInterpret = async (doc) => {
    setInterpretTarget(doc);
    setInterpretation(null);
    setInterpretErr("");
    setInterpreting(true);
    try {
      const data = await interpretExam(doc.id);
      setInterpretation(data);
    } catch (err) {
      setInterpretErr(docErrorMessage(err));
    } finally {
      setInterpreting(false);
    }
  };

  const handleConfirmDelete = async () => {
    const doc = deleteTarget;
    setDeleteTarget(null);
    if (!doc) return;
    setBusyId(doc.id);
    try {
      await deleteEhrFile(doc.id);
      toast.success("Exame removido.");
      await load();
    } catch (err) {
      toast.error(docErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={DOC_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      <div className={classes.headerRow}>
        <Typography variant="h6" className={classes.title}>
          <InsertDriveFileIcon fontSize="small" /> Exames anexados ({documents.length})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={handlePick}
        >
          Anexar exame
        </Button>
      </div>

      {loading ? (
        <CircularProgress size={24} />
      ) : documents.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          Nenhum exame anexado ainda. Clique em "Anexar exame" para subir um PDF ou imagem.
        </Typography>
      ) : (
        documents.map((doc) => (
          <Paper key={doc.id} variant="outlined" className={classes.card}>
            <TypeIcon doc={doc} />
            <div className={classes.fileInfo}>
              <div className={classes.fileName}>{doc.fileName || `Anexo #${doc.id}`}</div>
              <div className={classes.meta}>
                <Chip size="small" label={typeLabel(doc)} variant="outlined" style={{ marginRight: 8 }} />
                {fmtDateTime(doc.uploadedAt)}
              </div>
              {doc.description ? <div className={classes.desc}>{doc.description}</div> : null}
            </div>
            <div className={classes.actions}>
              {busyId === doc.id ? (
                <CircularProgress size={22} style={{ margin: 8 }} />
              ) : (
                <>
                  <Tooltip title="Interpretar com IA (resumo + alterações)">
                    <IconButton size="small" onClick={() => handleInterpret(doc)}>
                      <FindInPageIcon fontSize="small" style={{ color: "#6a1b9a" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ver">
                    <IconButton size="small" onClick={() => handleView(doc)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Baixar">
                    <IconButton size="small" onClick={() => handleDownload(doc)}>
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => setDeleteTarget(doc)}>
                      <DeleteOutlineIcon fontSize="small" style={{ color: "#d32f2f" }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </div>
          </Paper>
        ))
      )}

      <Typography variant="caption" className={classes.hint} component="div">
        Aceita PDF, PNG, JPG ou WEBP, até 10 MB por arquivo. Os arquivos ficam protegidos
        (acesso autenticado, isolado por clínica).
      </Typography>

      {/* Diálogo: descrição + confirmar upload */}
      <Dialog open={!!pendingFile} onClose={() => (!uploading ? setPendingFile(null) : null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Anexar exame</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ wordBreak: "break-word" }}>
            {pendingFile ? pendingFile.name : ""}
            {pendingFile ? ` (${Math.round(pendingFile.size / 1024)} KB)` : ""}
          </DialogContentText>
          <TextField
            label="Descrição (opcional)"
            placeholder="ex: Hemograma completo, Raio-X tórax..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            inputProps={{ maxLength: 2000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingFile(null)} disabled={uploading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          >
            {uploading ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: confirmar exclusão */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} fullScreen={isMobile}>
        <DialogTitle>Remover exame</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover "{deleteTarget ? deleteTarget.fileName : ""}"? Esta ação não pode ser desfeita
            (fica registrada na trilha de auditoria).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} style={{ color: "#d32f2f" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: interpretação por IA */}
      <Dialog open={!!interpretTarget} onClose={() => setInterpretTarget(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>🔬 Interpretação por IA — {interpretTarget ? interpretTarget.fileName : ""}</DialogTitle>
        <DialogContent dividers>
          {interpreting ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 16 }}>
              <CircularProgress size={22} /> Analisando o exame com IA…
            </div>
          ) : interpretErr ? (
            <Typography color="error">{interpretErr}</Typography>
          ) : interpretation ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Chip size="small" label={gravLabel(interpretation.gravidade)} style={gravStyle(interpretation.gravidade)} />
                <Typography variant="subtitle2">{interpretation.tipoExame}</Typography>
              </div>
              <Typography variant="body2" style={{ marginTop: 6 }}>
                {interpretation.resumo}
              </Typography>
              {interpretation.alteracoes && interpretation.alteracoes.length > 0 && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 10 }}>
                    Alterações
                  </Typography>
                  <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                    {interpretation.alteracoes.map((a, i) => (
                      <li key={i}>
                        <strong>{a.parametro}:</strong> {a.valor}
                        {a.referencia ? ` (ref: ${a.referencia})` : ""}
                        {a.status ? ` — ${a.status}` : ""}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {interpretation.condutaSugerida && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 10 }}>
                    Conduta sugerida
                  </Typography>
                  <Typography variant="body2">{interpretation.condutaSugerida}</Typography>
                </>
              )}
              {interpretation.observacao && (
                <Typography variant="body2" style={{ marginTop: 8, color: "#666" }}>
                  {interpretation.observacao}
                </Typography>
              )}
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  background: "#fff8e1",
                  color: "#8d6e00",
                  borderRadius: 6,
                  fontSize: 12.5
                }}
              >
                ⚠️ {interpretation.disclaimer}
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterpretTarget(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DocumentList;
